// =============================================================================
// RepairModal
//
// Consultant-led credit repair. For a client whose CBC user creation failed
// at registration (no cus_id, no MAP), the consultant enters the client's
// SSN to create the CBC user and pull credit so the MAP can be generated.
//
// -----------------------------------------------------------------------------
// FUTURE: client-led version (pending product decision)
//
// To convert to client-led (the client enters their own SSN on their own
// dashboard), three changes are needed:
//   1. Render this on the client dashboard, sourcing the client from
//      useAuth() instead of a passed-in client prop.
//   2. Change the backend route auth from authorizeAdmin to authenticateUser
//      only (src/api/routes/admin/repair/index.ts).
//   3. Move the repair action off the admin ClientRow onto the client view.
// The SSN field, the repair call, and result handling stay identical.
// -----------------------------------------------------------------------------
// =============================================================================

import { FunctionComponent, useState } from "react";
import { REPAIR_MODAL } from "src/constants/adminDashboard";

interface RepairModalProps {
  clientName: string;
  onConfirm: (ssn: string) => void;
  onCancel: () => void;
  loading: boolean;
}

const RepairModal: FunctionComponent<RepairModalProps> = ({
  clientName,
  onConfirm,
  onCancel,
  loading,
}) => {
  const [ssn, setSsn] = useState("");

  const canSubmit = ssn.replace(/\D/g, "").length === 9 && !loading;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">
          {REPAIR_MODAL.title}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {REPAIR_MODAL.body(clientName)}
        </p>

        <input
          type="password"
          inputMode="numeric"
          placeholder={REPAIR_MODAL.ssnPlaceholder}
          value={ssn}
          onChange={(e) => setSsn(e.target.value)}
          disabled={loading}
          className="border border-gray-300 rounded px-3 py-2 text-sm mb-6 w-full disabled:opacity-50"
        />

        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            {REPAIR_MODAL.cancel}
          </button>
          <button
            onClick={() => onConfirm(ssn)}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm text-white bg-primary rounded disabled:opacity-50"
          >
            {loading ? "Processing..." : REPAIR_MODAL.confirm}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepairModal;

